/**
 * Maps a link destination to a GA4 CTA event.
 *
 * Shared by the client-side click tracker and the server-side Measurement
 * Protocol endpoint, so both sides always agree on event names.
 */
export type CtaEventName =
  | "cta_request_demo"
  | "cta_contact_us"
  | "cta_explore_platform"
  | "cta_login";

export const CTA_EVENT_NAMES: readonly CtaEventName[] = [
  "cta_request_demo",
  "cta_contact_us",
  "cta_explore_platform",
  "cta_login",
];

/**
 * Form submission events. Paired with the CTA click events above so clicks and
 * completed submissions can be compared for conversion-rate optimisation.
 */
export type SubmitEventName =
  | "submit_request_demo"
  | "submit_contact_us"
  | "submit_free_trial"
  | "submit_report_request"
  | "submit_gated_download"
  | "submit_reports_notify";

export const SUBMIT_EVENT_NAMES: readonly SubmitEventName[] = [
  "submit_request_demo",
  "submit_contact_us",
  "submit_free_trial",
  "submit_report_request",
  "submit_gated_download",
  "submit_reports_notify",
];

/**
 * Engagement / diagnostics events: form funnel steps (start, abandon, failure)
 * and outbound link clicks. Tracked both client-side and server-side.
 */
export type EngagementEventName =
  | "form_start"
  | "form_abandon"
  | "form_submit_failure"
  | "outbound_click"
  | "video_start"
  | "video_complete";

export const ENGAGEMENT_EVENT_NAMES: readonly EngagementEventName[] = [
  "form_start",
  "form_abandon",
  "form_submit_failure",
  "outbound_click",
  "video_start",
  "video_complete",
];

/** Reason codes for `form_submit_failure`, used to diagnose conversion loss. */
export type FormFailureReason =
  | "validation_error"
  | "network_error"
  | "rate_limited"
  | "server_error"
  | "provider_error"
  | "unknown_error";

export type TrackedEventName = CtaEventName | SubmitEventName | EngagementEventName;

export const TRACKED_EVENT_NAMES: readonly TrackedEventName[] = [
  ...CTA_EVENT_NAMES,
  ...SUBMIT_EVENT_NAMES,
  ...ENGAGEMENT_EVENT_NAMES,
];

/** Map an HTTP status to a coarse failure reason code. */
export function failureReasonForStatus(status: number): FormFailureReason {
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server_error";
  if (status >= 400) return "provider_error";
  return "unknown_error";
}

const LOGIN_HOST = "mlpn.perceptica.com";

/** Normalise a same-site path: strip query/hash, trailing slash and the /bg prefix. */
function normalisePath(pathname: string): string {
  const clean = pathname.split("?")[0]!.split("#")[0]!.replace(/\/+$/, "");
  return clean === "" ? "/" : clean;
}

export function localeFromPathname(pathname: string): "en" | "bg" {
  return /^\/bg(\/|$)/.test(pathname) ? "bg" : "en";
}

/**
 * Returns the CTA event for a destination, or null when the link is not a
 * tracked call to action.
 *
 * `href` may be absolute or root-relative.
 */
export function ctaEventForHref(href: string): CtaEventName | null {
  if (!href) return null;

  let pathname = href;
  if (/^https?:\/\//i.test(href)) {
    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return null;
    }
    if (url.hostname === LOGIN_HOST) return "cta_login";
    if (!/(^|\.)perceptica\.com$/i.test(url.hostname)) return null;
    pathname = url.pathname;
  } else if (!href.startsWith("/")) {
    return null;
  }

  const path = normalisePath(pathname).replace(/^\/bg(?=\/|$)/, "") || "/";

  if (path === "/request-a-demo") return "cta_request_demo";
  if (path === "/contact-us") return "cta_contact_us";
  if (path === "/media-monitoring/platform") return "cta_explore_platform";
  return null;
}

const INTERNAL_HOST_RE = /(^|\.)perceptica\.com$/i;

export type OutboundLinkInfo = { url: string; domain: string };

/**
 * Returns destination info when `href` points to another site, otherwise null.
 * Same-site links, anchors, mailto: and tel: are not outbound clicks.
 */
export function outboundLinkInfo(href: string, currentHost: string): OutboundLinkInfo | null {
  if (!href || href.startsWith("#") || href.startsWith("/")) return null;
  if (/^(mailto|tel|sms|javascript):/i.test(href)) return null;
  let url: URL;
  try {
    url = new URL(href, `https://${currentHost}`);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(url.protocol)) return null;
  if (url.hostname === currentHost) return null;
  if (INTERNAL_HOST_RE.test(url.hostname) && INTERNAL_HOST_RE.test(currentHost)) return null;
  return { url: url.href.slice(0, 500), domain: url.hostname };
}

/**
 * Best-effort description of where on the page a link sits (header, footer,
 * nav, main content, ...), so outbound clicks can be attributed to a location.
 */
export function linkLocation(el: Element): string {
  const named = el.closest("[data-analytics-location]");
  const explicit = named?.getAttribute("data-analytics-location");
  if (explicit) return explicit.slice(0, 60);
  if (el.closest("header")) return "header";
  if (el.closest("footer")) return "footer";
  if (el.closest("nav")) return "nav";
  if (el.closest("aside")) return "sidebar";
  if (el.closest("form")) return "form";
  if (el.closest("main")) return "main";
  return "other";
}
